import * as Tone from "tone";
import { DIAGNOSTIC_CLICK_VELOCITY } from "./constants";

/**
 * Every Tone.js access in the app goes through this adapter.
 *
 * Two reasons:
 *  - version drift is contained in one file (we pin tone to 15.1.22)
 *  - AudioEngine can be unit tested against a fake adapter, with no Web Audio
 *
 * API semantics verified against Tone.js 15.1.22 docs and the dev sources:
 *  - pause() keeps the tick counter; stop() resets it to 0
 *  - neither touches the scheduled-event timelines
 *  - cancel(after) ALSO removes repeated events whose startTime is after
 *    `after`, so it is never used here (lint-enforced)
 *  - clear(id) removes one event by id — used only in dispose()
 */

export interface ParamLike {
  readonly value: number;
  setValue(value: number): void;
  rampTo(value: number, rampSec: number, startTimeSec?: number): void;
}

export interface NodeLike {
  dispose(): void;
}

export interface GainLike extends NodeLike {
  readonly gain: ParamLike;
}

export interface ClickVoiceLike extends NodeLike {
  /** `midi` is null for unpitched percussion. */
  trigger(midi: number | null, durationSec: number, timeSec: number, velocity: number): void;
  releaseAll(): void;
}

/** Phase 1A drum voices. Synthesised — no samples, so nothing to fetch. */
export type DrumVoiceKind = "kick" | "pulse" | "percussion" | "accent";

export interface TransportLike {
  readonly ppq: number;
  readonly bpm: number;
  setBpm(bpm: number): void;
  /** Current tick. Diagnostics and pause-position only — never reservation math. */
  readonly ticks: number;
  setTicks(ticks: number): void;
  start(): void;
  pause(): void;
  stop(): void;
  scheduleRepeat(callback: (timeSec: number) => void, interval: string): number;
  clear(id: number): void;
  getTicksAtTime(timeSec: number): number;
}

export interface ToneAdapter {
  start(): Promise<void>;
  contextState(): string;
  resumeContext(): Promise<void>;
  /** AudioContext.currentTime. Diagnostics / drain timing only. */
  nowSec(): number;
  lookAheadSec(): number;
  baseLatencySec(): number;
  outputLatencySec(): number | null;
  transport(): TransportLike;
  /** Master chain: Gain -> Limiter -> Destination. Returns the input gain node. */
  createMasterChain(thresholdDb: number): { input: GainLike; nodes: NodeLike[] };
  /** Must be called after createMasterChain; the voice is routed into the master gain. */
  createClickVoice(): ClickVoiceLike;
  /** Must be called after createMasterChain. One voice per rhythm role. */
  createDrumVoice(kind: DrumVoiceKind): ClickVoiceLike;
}

// ---------------------------------------------------------------------------
// Real implementation
// ---------------------------------------------------------------------------

function wrapParam(param: Tone.Param<"gain"> | Tone.Signal<"gain">): ParamLike {
  return {
    get value() {
      return param.value;
    },
    setValue(value: number) {
      param.value = value;
    },
    rampTo(value: number, rampSec: number, startTimeSec?: number) {
      if (startTimeSec === undefined) {
        param.rampTo(value, rampSec);
      } else {
        param.rampTo(value, rampSec, startTimeSec);
      }
    },
  };
}

class RealTransport implements TransportLike {
  private readonly t = Tone.getTransport();

  get ppq(): number {
    return this.t.PPQ;
  }

  get bpm(): number {
    return this.t.bpm.value;
  }

  setBpm(bpm: number): void {
    this.t.bpm.value = bpm;
  }

  get ticks(): number {
    return this.t.ticks;
  }

  setTicks(ticks: number): void {
    // Safe only while paused/stopped: the setter restarts the transport if it is
    // currently started. All callers pause first.
    this.t.ticks = ticks;
  }

  start(): void {
    this.t.start();
  }

  pause(): void {
    this.t.pause();
  }

  stop(): void {
    this.t.stop();
  }

  scheduleRepeat(callback: (timeSec: number) => void, interval: string): number {
    return this.t.scheduleRepeat(callback, interval);
  }

  clear(id: number): void {
    this.t.clear(id);
  }

  getTicksAtTime(timeSec: number): number {
    return this.t.getTicksAtTime(timeSec);
  }
}

export function createToneAdapter(): ToneAdapter {
  const transport = new RealTransport();
  /** Set by createMasterChain, consumed by createClickVoice. */
  let masterGainNode: Tone.Gain | null = null;

  return {
    async start() {
      await Tone.start();
    },
    contextState() {
      return Tone.getContext().state;
    },
    async resumeContext() {
      await Tone.getContext().resume();
    },
    nowSec() {
      return Tone.getContext().currentTime;
    },
    lookAheadSec() {
      const value = Tone.getContext().lookAhead;
      return typeof value === "number" ? value : 0;
    },
    baseLatencySec() {
      const raw: unknown = Tone.getContext().rawContext;
      if (raw !== null && typeof raw === "object" && "baseLatency" in raw) {
        const latency: unknown = raw.baseLatency;
        if (typeof latency === "number") return latency;
      }
      return 0;
    },
    outputLatencySec() {
      // Not exposed by every browser; the UI shows "非対応" when null.
      const raw: unknown = Tone.getContext().rawContext;
      if (raw !== null && typeof raw === "object" && "outputLatency" in raw) {
        const latency: unknown = raw.outputLatency;
        if (typeof latency === "number") return latency;
      }
      return null;
    },
    transport() {
      return transport;
    },
    createMasterChain(thresholdDb: number) {
      // Gain starts at 0: init and resume are the only places it is raised.
      const gain = new Tone.Gain(0);
      const limiter = new Tone.Limiter(thresholdDb);
      gain.connect(limiter);
      limiter.connect(Tone.getDestination());
      masterGainNode = gain;
      const input: GainLike = {
        gain: wrapParam(gain.gain),
        dispose() {
          gain.dispose();
          masterGainNode = null;
        },
      };
      return { input, nodes: [input, { dispose: () => limiter.dispose() }] };
    },
    createClickVoice() {
      const synth = new Tone.Synth({
        oscillator: { type: "sine" },
        envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.02 },
        volume: -12,
      });
      // Route through the master chain so the limiter and master gain apply.
      synth.disconnect();
      if (masterGainNode !== null) {
        synth.connect(masterGainNode);
      } else {
        throw new Error("createClickVoice() called before createMasterChain()");
      }
      return {
        trigger(midi: number | null, durationSec: number, timeSec: number, velocity: number) {
          synth.triggerAttackRelease(
            Tone.Frequency(midi ?? 84, "midi").toFrequency(),
            durationSec,
            timeSec,
            velocity,
          );
        },
        releaseAll() {
          synth.triggerRelease();
        },
        dispose() {
          synth.dispose();
        },
      };
    },

    /**
     * Phase 1A kit. Synthesised rather than sampled: nothing to fetch, so
     * startup stays deterministic and the bundle stays small. These are meant
     * to be distinguishable, not finished — Phase 1C designs the real timbres.
     */
    createDrumVoice(kind: DrumVoiceKind): ClickVoiceLike {
      if (masterGainNode === null) {
        throw new Error("createDrumVoice() called before createMasterChain()");
      }
      const destination = masterGainNode;

      if (kind === "kick") {
        const kick = new Tone.MembraneSynth({
          pitchDecay: 0.035,
          octaves: 5,
          oscillator: { type: "sine" },
          envelope: { attack: 0.001, decay: 0.32, sustain: 0, release: 0.2 },
          volume: -6,
        });
        kick.disconnect();
        kick.connect(destination);
        return {
          trigger: (_midi, durationSec, timeSec, velocity) => {
            kick.triggerAttackRelease("C1", Math.min(0.3, durationSec), timeSec, velocity);
          },
          releaseAll: () => {
            kick.triggerRelease();
          },
          dispose: () => {
            kick.dispose();
          },
        };
      }

      if (kind === "pulse") {
        // Closed, metallic tick: the timekeeper.
        const pulse = new Tone.MetalSynth({
          envelope: { attack: 0.001, decay: 0.06, release: 0.02 },
          harmonicity: 5.1,
          modulationIndex: 24,
          resonance: 6000,
          octaves: 1.2,
          volume: -26,
        });
        pulse.disconnect();
        pulse.connect(destination);
        return {
          trigger: (_midi, durationSec, timeSec, velocity) => {
            pulse.triggerAttackRelease(Math.min(0.08, durationSec), timeSec, velocity);
          },
          releaseAll: () => {
            pulse.triggerRelease();
          },
          dispose: () => {
            pulse.dispose();
          },
        };
      }

      if (kind === "percussion") {
        // Filtered noise: drier and lower than the pulse, so the two separate.
        const noise = new Tone.NoiseSynth({
          noise: { type: "pink" },
          envelope: { attack: 0.002, decay: 0.12, sustain: 0, release: 0.05 },
          volume: -20,
        });
        const filter = new Tone.Filter({ frequency: 2400, type: "bandpass", Q: 1.2 });
        noise.disconnect();
        noise.connect(filter);
        filter.connect(destination);
        return {
          trigger: (_midi, durationSec, timeSec, velocity) => {
            noise.triggerAttackRelease(Math.min(0.15, durationSec), timeSec, velocity);
          },
          releaseAll: () => {
            noise.triggerRelease();
          },
          dispose: () => {
            noise.dispose();
            filter.dispose();
          },
        };
      }

      // accent: a short pitched blip that punctuates without carrying the beat.
      const accent = new Tone.Synth({
        oscillator: { type: "triangle" },
        envelope: { attack: 0.002, decay: 0.18, sustain: 0, release: 0.08 },
        volume: -20,
      });
      accent.disconnect();
      accent.connect(destination);
      return {
        trigger: (midi, durationSec, timeSec, velocity) => {
          accent.triggerAttackRelease(
            Tone.Frequency(midi ?? 72, "midi").toFrequency(),
            Math.min(0.2, durationSec),
            timeSec,
            velocity,
          );
        },
        releaseAll: () => {
          accent.triggerRelease();
        },
        dispose: () => {
          accent.dispose();
        },
      };
    },
  };
}

export { DIAGNOSTIC_CLICK_VELOCITY };
